import { useEffect, useMemo, useState } from "react";
import { Check, X, Trash2, AlertTriangle, Pencil, Save, XCircle, Loader2 } from "lucide-react";

export interface QueueItem {
	qid: string;
	operation: string;
	args: string;
	description: string;
	timestamp: number;
}

interface ApprovalDialogProps {
	queue: QueueItem[];
	onApprove: (selectedQids: string[]) => void;
	onReject?: () => void;
	onRemoveItem: (qid: string) => void;
	onUpdateItem?: (qid: string, updates: { args: string; description: string }) => Promise<void> | void;
	onClose?: () => void;
	title?: string;
	subtitle?: string;
	approveLabel?: string;
	approving?: boolean;
}

export default function ApprovalDialog({
	queue,
	onApprove,
	onReject,
	onRemoveItem,
	onUpdateItem,
	onClose,
	title,
	subtitle,
	approveLabel,
	approving = false,
}: ApprovalDialogProps) {
	const [selectedQids, setSelectedQids] = useState<Set<string>>(
		new Set(queue.map((q) => q.qid)),
	);
	const [editingQid, setEditingQid] = useState<string | null>(null);
	const [draftArgs, setDraftArgs] = useState("");
	const [draftDescription, setDraftDescription] = useState("");
	const [savingEdit, setSavingEdit] = useState(false);
	const interactionLocked = approving || savingEdit;

	useEffect(() => {
		const queueQidSet = new Set(queue.map((q) => q.qid));
		setSelectedQids((prev) => {
			const next = new Set<string>();
			for (const qid of prev) {
				if (queueQidSet.has(qid)) {
					next.add(qid);
				}
			}
			for (const qid of queueQidSet) {
				next.add(qid);
			}
			return next;
		});

		if (editingQid && !queueQidSet.has(editingQid)) {
			setEditingQid(null);
			setDraftArgs("");
			setDraftDescription("");
		}
	}, [queue, editingQid]);

	const editingItem = useMemo(
		() => queue.find((item) => item.qid === editingQid) ?? null,
		[queue, editingQid],
	);

	const toggleItem = (qid: string) => {
		if (interactionLocked) return;
		setSelectedQids((prev) => {
			const next = new Set(prev);
			if (next.has(qid)) next.delete(qid);
			else next.add(qid);
			return next;
		});
	};

	const startEdit = (item: QueueItem) => {
		if (interactionLocked) return;
		setEditingQid(item.qid);
		setDraftArgs(item.args);
		setDraftDescription(item.description);
	};

	const cancelEdit = () => {
		if (approving) return;
		setEditingQid(null);
		setDraftArgs("");
		setDraftDescription("");
	};

	const saveEdit = async () => {
		if (!onUpdateItem || !editingItem || interactionLocked) return;
		setSavingEdit(true);
		try {
			await onUpdateItem(editingItem.qid, {
				args: draftArgs,
				description: draftDescription.trim() || editingItem.description,
			});
			cancelEdit();
		} finally {
			setSavingEdit(false);
		}
	};

	const operationColor = (op: string) => {
		if (op.startsWith("add")) return "text-emerald-400 bg-emerald-400/10";
		if (op.startsWith("delete")) return "text-red-400 bg-red-400/10";
		return "text-amber-400 bg-amber-400/10";
	};

	const operationLabel = (op: string) => {
		const labels: Record<string, string> = {
			addNode: "Add Node",
			addRelation: "Add Relation",
			updateNode: "Update Node",
			deleteNode: "Delete Node",
			deleteRelation: "Delete Relation",
			attachNodeProperties: "Attach Props",
			attachRelationProperties: "Attach Rel. Props",
		};
		return labels[op] ?? op;
	};

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
			<div className="w-full max-w-lg bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden">
				{/* Header */}
				<div className="flex items-center gap-3 px-6 py-4 border-b border-slate-700">
					<AlertTriangle className="w-5 h-5 text-amber-400" />
					<div>
						<h3 className="text-white font-semibold">
							{title ?? "Pending Modifications"}
						</h3>
						<p className="text-slate-400 text-sm">
							{subtitle ?? "Review and approve the following changes"}
						</p>
					</div>
					{onClose && (
						<button
							onClick={onClose}
							disabled={approving}
							className="ml-auto p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700/70 transition-all"
							title="Close"
						>
							<X className="w-4 h-4" />
						</button>
					)}
				</div>

				{/* Queue Items */}
				<div className="max-h-80 overflow-y-auto p-4 space-y-2">
					{queue.length === 0 ? (
						<p className="text-slate-500 text-center py-8">
							No pending modifications
						</p>
					) : (
						queue.map((item) => (
							<div
								key={item.qid}
								className={`flex items-start gap-3 p-3 rounded-xl border transition-all ${interactionLocked ? "cursor-not-allowed" : "cursor-pointer"} ${
									selectedQids.has(item.qid)
										? "bg-slate-700/50 border-blue-500/30"
										: "bg-slate-800/50 border-slate-700/50 opacity-60"
								}`}
								onClick={() => toggleItem(item.qid)}
							>
								<div
									className={`mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
										selectedQids.has(item.qid)
											? "bg-blue-500 border-blue-500"
											: "border-slate-500"
									}`}
								>
									{selectedQids.has(item.qid) && (
										<Check className="w-3 h-3 text-white" />
									)}
								</div>
								<div className="flex-1 min-w-0">
									<div className="flex items-center gap-2 mb-1">
										<span
											className={`text-xs font-medium px-2 py-0.5 rounded-full ${operationColor(item.operation)}`}
										>
											{operationLabel(item.operation)}
										</span>
									</div>
									{editingQid === item.qid ? (
										<div className="space-y-2" onClick={(e) => e.stopPropagation()}>
											<textarea
												value={draftDescription}
												onChange={(e) => setDraftDescription(e.target.value)}
												disabled={interactionLocked}
												className="w-full px-2 py-1.5 text-xs rounded-lg bg-slate-900/80 border border-slate-600 text-slate-200 resize-y min-h-14"
												placeholder="Description"
											/>
											<textarea
												value={draftArgs}
												onChange={(e) => setDraftArgs(e.target.value)}
												disabled={interactionLocked}
												className="w-full px-2 py-1.5 text-xs font-mono rounded-lg bg-slate-900/80 border border-slate-600 text-slate-200 resize-y min-h-20"
												placeholder='{"nid":"..."}'
											/>
											<div className="flex items-center gap-2">
												<button
													onClick={saveEdit}
													disabled={interactionLocked}
													className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white"
												>
													<Save className="w-3.5 h-3.5" />
													Save
												</button>
												<button
													onClick={cancelEdit}
													disabled={approving}
													className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700"
												>
													<XCircle className="w-3.5 h-3.5" />
													Cancel
												</button>
											</div>
										</div>
									) : (
										<>
											<p className="text-sm text-slate-300 truncate">{item.description}</p>
											<p className="text-xs text-slate-500 mt-1 truncate font-mono">{item.args}</p>
										</>
									)}
								</div>
								<div className="shrink-0 flex items-center gap-1">
									{onUpdateItem && editingQid !== item.qid && (
										<button
											onClick={(e) => {
												e.stopPropagation();
												startEdit(item);
											}}
											disabled={interactionLocked}
											className="p-1.5 rounded-lg text-slate-500 hover:text-blue-400 hover:bg-blue-400/10 transition-all"
											title="Edit queue item"
										>
											<Pencil className="w-4 h-4" />
										</button>
									)}
									<button
										onClick={(e) => {
											e.stopPropagation();
											if (interactionLocked) return;
											onRemoveItem(item.qid);
										}}
										disabled={interactionLocked}
										className="p-1.5 rounded-lg text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-all"
										title="Remove from queue"
									>
										<Trash2 className="w-4 h-4" />
									</button>
								</div>
							</div>
						))
					)}
				</div>

				{/* Actions */}
				<div className="flex items-center gap-3 px-6 py-4 border-t border-slate-700 bg-slate-800/80">
					{onReject && (
						<button
							onClick={onReject}
							disabled={approving}
							className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-slate-600 text-slate-300 hover:bg-slate-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<X className="w-4 h-4" />
							Reject All
						</button>
					)}
					<button
						onClick={() => onApprove(Array.from(selectedQids))}
						disabled={selectedQids.size === 0 || approving}
						className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-medium transition-all"
					>
						{approving ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Executing...
							</>
						) : (
							<>
								<Check className="w-4 h-4" />
								{approveLabel ?? "Approve"}{" "}
								{selectedQids.size > 0 ? `(${selectedQids.size})` : ""}
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
}
